import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import RouteRoundedIcon from "@mui/icons-material/RouteRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import { useParams } from "react-router-dom";
import { activityApi } from "../../api/activity";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectCapabilityEnabled } from "../../utils/projectCapabilities";

import EditorialIntro from "../../components/dashboard/EditorialIntro";
import ResponsiveListControls from "../../components/dashboard/ResponsiveListControls";
import { useListScrollRestoration, usePersistentListValue } from "../../utils/listViewState";
const BRAND = { orange: "#F35A3A", dark: "#19242F" };
const PAGE_SIZE = 40;
const CATEGORY_LABELS = {
  navigation: "Navegación",
  commercial: "Comercial",
  auth: "Accesos",
  user: "Usuarios",
  project: "Proyecto",
  landing: "Página",
  product: "Productos / servicios",
  order: "Pedidos",
  payment: "Pagos",
  appointment: "Agenda",
  erp: "ERP",
  cash: "Caja",
  stock: "Stock",
  billing: "Billing",
  push: "Notificaciones",
  integration: "Integraciones",
  membership: "Membresías",
  system: "Sistema",
};

function dateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium", timeStyle: "medium" }).format(date);
}

function actorLabel(event) {
  const actor = event?.actor || {};
  return actor.name || actor.email || (actor.type === "visitor" ? "Visitante web" : actor.type === "customer" ? "Cliente registrado" : actor.type === "webhook" ? "Webhook" : "Sistema");
}

function Metric({ icon, title, value, helper }) {
  return (
    <Card elevation={0} sx={{ height: "100%", borderRadius: { xs: 3, sm: 4 }, border: "1px solid rgba(25,36,47,.08)" }}>
      <CardContent sx={{ p: { xs: 1.25, sm: 2.1 }, "&:last-child": { pb: { xs: 1.25, sm: 2.1 } } }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: .65, sm: 1.25 }} alignItems={{ xs: "flex-start", sm: "center" }}>
          <Box sx={{ width: { xs: 32, sm: 42 }, height: { xs: 32, sm: 42 }, borderRadius: 2.3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.10)", color: BRAND.orange, "& svg": { fontSize: { xs: 18, sm: 23 } } }}>{icon}</Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ display: "block", color: "rgba(25,36,47,.58)", fontWeight: 900, lineHeight: 1.2 }}>{title}</Typography>
            <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: { xs: 22, sm: 28 }, lineHeight: 1.1, mt: .15 }}>{value}</Typography>
            <Typography variant="caption" sx={{ display: { xs: "none", sm: "block" }, color: "rgba(25,36,47,.50)", mt: .2 }}>{helper}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function ProjectActivityPage() {
  const { projectId } = useParams();
  const { projects, activeProject } = useProjects();
  const effectiveProject = React.useMemo(() => {
    const matches = (project) => [project?.slug, project?.id, project?._id]
      .filter(Boolean)
      .some((value) => String(value) === String(projectId));
    return (projects || []).find(matches) || (matches(activeProject) ? activeProject : null);
  }, [projects, activeProject, projectId]);
  const membershipsEnabled = isProjectCapabilityEnabled(effectiveProject, "memberships");
  const [items, setItems] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [journeys, setJourneys] = React.useState([]);
  const [pagination, setPagination] = React.useState({ page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = usePersistentListValue(`activity:${projectId}:filters`, { category: "", source: "", q: "" });
  const categoryFilterValue = membershipsEnabled || filters.category !== "membership" ? filters.category : "";
  const [page, setPage] = usePersistentListValue(`activity:${projectId}:page`, 1);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = { page, pageSize: PAGE_SIZE, ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value)) };
      const [activityResponse, summaryResponse, journeysResponse] = await Promise.all([
        activityApi.project(projectId, params),
        activityApi.projectSummary(projectId, 24),
        activityApi.projectJourneys(projectId, { hours: 168, limit: 20 }),
      ]);
      setItems(activityResponse.items || []);
      setPagination(activityResponse.pagination || { page, pages: 1, total: 0 });
      setSummary(summaryResponse.summary || null);
      setJourneys(journeysResponse.items || []);
    } catch (err) {
      setError(err.message || "No se pudo cargar la actividad del proyecto");
    } finally {
      setLoading(false);
    }
  }, [projectId, page, filters]);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    if (!membershipsEnabled && filters.category === "membership") {
      setFilters((current) => ({ ...current, category: "" }));
      setPage(1);
    }
  }, [membershipsEnabled, filters.category, setFilters, setPage]);

  useListScrollRestoration(`activity:${projectId}`, !loading);

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  return (
    <Stack spacing={3}>
      <EditorialIntro
        title="Actividad y recorridos"
        titleVariant="h3"
        context="Visitas y operaciones del proyecto en un solo historial."
        helpTitle="Qué incluye este historial"
        helpText="Reúne visitas a la web, consultas, carrito, pedidos y operaciones realizadas por dueños o empleados para reconstruir el recorrido completo."
        action={<Button onClick={load} disabled={loading} startIcon={<RefreshRoundedIcon />} variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}>Actualizar</Button>}
      />

      {error && <Alert severity="error">{error}</Alert>}

      <Grid container spacing={{ xs: 1, sm: 2 }}>
        <Grid item xs={6} lg={3}><Metric icon={<HistoryRoundedIcon />} title="Eventos en 24 h" value={summary?.total ?? "—"} helper="Web y operación" /></Grid>
        <Grid item xs={6} lg={3}><Metric icon={<PeopleAltRoundedIcon />} title="Visitantes únicos" value={summary?.uniqueVisitors ?? "—"} helper="Navegadores anónimos" /></Grid>
        <Grid item xs={6} lg={3}><Metric icon={<BoltRoundedIcon />} title="Alta intención" value={summary?.highIntent ?? "—"} helper="WhatsApp, carrito y checkout" /></Grid>
        <Grid item xs={6} lg={3}><Metric icon={<DashboardRoundedIcon />} title="Usuarios del panel" value={summary?.uniquePanelUsers ?? "—"} helper="Dueños y empleados activos" /></Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: { xs: 1.35, sm: 2.5 }, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
          <RouteRoundedIcon sx={{ color: BRAND.orange }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark, fontSize: { xs: 18, sm: 24 } }}>Recorridos recientes</Typography>
            <Typography variant="body2" sx={{ display: { xs: "none", sm: "block" }, color: "rgba(25,36,47,.58)" }}>Abrí una sesión para ver su secuencia completa.</Typography>
          </Box>
        </Stack>
        <Stack spacing={1}>
          {journeys.slice(0, 10).map((journey) => (
            <Box
              component="details"
              key={`${journey.channel}-${journey.sessionId}`}
              sx={{ p: { xs: 1.1, sm: 1.5 }, borderRadius: 3, bgcolor: "#F6F7FB", "&[open] .journey-chevron": { transform: "rotate(180deg)" } }}
            >
              <Stack component="summary" direction="row" justifyContent="space-between" spacing={1} alignItems="center" sx={{ minHeight: 42, cursor: "pointer", listStyle: "none", "&::-webkit-details-marker": { display: "none" } }}>
                <Stack direction="row" spacing={.65} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                  <Chip size="small" icon={journey.channel === "storefront" ? <StorefrontRoundedIcon /> : <DashboardRoundedIcon />} label={journey.channel === "storefront" ? "Web pública" : "Panel"} />
                  <Chip size="small" variant="outlined" label={`${journey.stepsCount || 0} pasos`} />
                  {journey.highIntent ? <Chip size="small" color="success" label={`${journey.highIntent} comerciales`} /> : null}
                </Stack>
                <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                  <Typography variant="caption" sx={{ display: { xs: "none", sm: "block" }, color: "rgba(25,36,47,.5)" }}>{dateTime(journey.lastAt)}</Typography>
                  <Typography className="journey-chevron" component="span" sx={{ display: { xs: "inline-block", sm: "none" }, color: "rgba(25,36,47,.5)", transition: "transform .18s ease" }}>⌄</Typography>
                </Box>
              </Stack>
              <Typography variant="body2" sx={{ mt: .85, color: "rgba(25,36,47,.66)", lineHeight: 1.55, overflowWrap: "anywhere" }}>
                {(journey.steps || []).map((step) => step.title || step.action).join(" → ") || "Sin pasos visibles"}
              </Typography>
              <Typography variant="caption" sx={{ display: { xs: "block", sm: "none" }, mt: .65, color: "rgba(25,36,47,.5)" }}>{dateTime(journey.lastAt)}</Typography>
            </Box>
          ))}
          {!loading && !journeys.length && <Alert severity="info">Todavía no hay sesiones con recorrido registrado.</Alert>}
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 1.1, sm: 2.5 }, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
        <ResponsiveListControls
          search={<TextField fullWidth size="small" placeholder="Buscar actividad" inputProps={{ "aria-label": "Buscar actividad, persona o producto" }} value={filters.q} onChange={(event) => updateFilter("q", event.target.value)} />}
          activeFilterCount={[filters.category, filters.source].filter(Boolean).length}
          onClear={() => {
            setFilters((current) => ({ ...current, category: "", source: "" }));
            setPage(1);
          }}
          drawerTitle="Filtrar actividad"
        >
          <FormControl size="small" sx={{ minWidth: { sm: 180 } }}><InputLabel>Categoría</InputLabel><Select label="Categoría" value={categoryFilterValue} onChange={(event) => updateFilter("category", event.target.value)}><MenuItem value="">Todas</MenuItem>{Object.entries(CATEGORY_LABELS).filter(([key]) => membershipsEnabled || key !== "membership").map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}</Select></FormControl>
          <FormControl size="small" sx={{ minWidth: { sm: 160 } }}><InputLabel>Origen</InputLabel><Select label="Origen" value={filters.source} onChange={(event) => updateFilter("source", event.target.value)}><MenuItem value="">Todos</MenuItem><MenuItem value="storefront">Web pública</MenuItem><MenuItem value="panel">Panel</MenuItem><MenuItem value="webhook">Webhook</MenuItem><MenuItem value="api">API / sistema</MenuItem></Select></FormControl>
        </ResponsiveListControls>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 1.35, sm: 2.5 }, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
          <Box><Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark, fontSize: { xs: 18, sm: 24 } }}>Historial completo</Typography><Typography variant="body2" sx={{ color: "rgba(25,36,47,.56)" }}>{pagination.total || 0} eventos</Typography></Box>
          {loading && <CircularProgress size={24} />}
        </Stack>
        <Stack spacing={1}>
          {items.map((event) => (
            <Box component="details" key={event.id} sx={{ p: { xs: 1.05, sm: 1.5 }, borderRadius: 3, bgcolor: "#F6F7FB", border: "1px solid rgba(25,36,47,.05)", "&[open] .event-detail-chevron": { transform: "rotate(180deg)" } }}>
              <Stack component="summary" direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start" sx={{ minHeight: 46, cursor: "pointer", listStyle: "none", "&::-webkit-details-marker": { display: "none" } }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 950, color: BRAND.dark, lineHeight: 1.25 }}>{event.title}</Typography>
                  <Stack direction="row" spacing={.55} flexWrap="wrap" useFlexGap sx={{ mt: .65 }}>
                    <Chip size="small" label={CATEGORY_LABELS[event.category] || event.category} />
                    <Chip size="small" variant="outlined" label={event.source === "storefront" ? "Web pública" : event.source === "panel" ? "Panel" : event.source} />
                  </Stack>
                </Box>
                <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                  <Typography variant="caption" sx={{ color: "rgba(25,36,47,.52)", fontWeight: 800 }}>{dateTime(event.occurredAt)}</Typography>
                  <Typography className="event-detail-chevron" sx={{ color: "rgba(25,36,47,.45)", lineHeight: 1, transition: "transform .18s ease" }}>⌄</Typography>
                </Box>
              </Stack>
              {event.description ? <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)", mt: .8, lineHeight: 1.5 }}>{event.description}</Typography> : null}
              <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap sx={{ mt: .85 }}>
                <Chip size="small" variant="outlined" label={actorLabel(event)} />
                {event.action ? <Chip size="small" variant="outlined" label={event.action} /> : null}
              </Stack>
            </Box>
          ))}
          {!loading && !items.length && <Alert severity="info">No hay actividad para estos filtros.</Alert>}
        </Stack>
        {pagination.pages > 1 && <Stack alignItems="center" sx={{ mt: 2 }}><Pagination size="small" page={pagination.page || page} count={pagination.pages} onChange={(_event, value) => setPage(value)} color="primary" /></Stack>}
      </Paper>
    </Stack>
  );
}
